import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { Box, TextField, Tooltip, Typography } from '@mui/material'
import { Card, CardTitle } from 'components'
import { TAttribute } from 'types'
import { TRule } from 'utils'
import { RootState } from 'store'
import { getRangeTooltipMessage } from 'utils/helpers/validationFunctions'
import { getBlendsAttributeWarnings } from 'pages/Blends/pages/BlendDetails/validations'
import {
  MainBox,
  HeaderContainer,
  HeaderParagraph,
  BodyContainer,
} from './BlendParameters.styled'
import * as sxStyles from './BlendParameters.styled'

const disabledRow = (attribute: TAttribute) => (
  <Typography>
    {attribute.range_min} - {attribute.range_max}
  </Typography>
)

const editAbleRow = (
  attribute: TAttribute,
  index: number,
  control: any,
  errors: any,
  dirtyFields: any,
  trigger: any,
  attributeWarnings: Record<string, { range_min?: string; range_max?: string }>,
  validationRules: Record<string, TRule>
) => {
  const isRangeMinDirty = !!dirtyFields?.attributes?.[index]?.range_min
  const isRangeMaxDirty = !!dirtyFields?.attributes?.[index]?.range_max

  return (
    <Box sx={sxStyles.editAbleRowBoxWrapper}>
      {/* Hidden field to store attribute name */}
      <Controller
        name={`attributes.${index}.name`}
        control={control}
        defaultValue={attribute.name}
        render={({ field }) => <input type="hidden" {...field} />}
      />
      <Box sx={sxStyles.controllerWithWrapperBox}>
        <Typography>From</Typography>
        <Controller
          name={`attributes.${index}.range_min`}
          control={control}
          defaultValue={attribute.range_min}
          render={({ field }) => (
            <Tooltip
              title={getRangeTooltipMessage(
                validationRules,
                attribute.name,
                'range_min'
              )}
              arrow
              placement="top"
            >
              <TextField
                sx={{
                  ...sxStyles.textFieldStyle,
                  ...sxStyles.rightHelperTextStyle,
                  '& .MuiInputBase-root': {
                    ...sxStyles.textFieldStyle['& .MuiInputBase-root'],
                    backgroundColor: isRangeMinDirty
                      ? (theme) => theme.palette.custom.inputChanged
                      : 'unset',
                  },
                  ...(attributeWarnings?.[attribute.name]?.range_min &&
                    sxStyles.warningColor),
                }}
                {...field}
                onChange={(e) => {
                  field.onChange(e.target.value.trim())
                  void trigger(`attributes.${index}.range_min`)
                  void trigger(`attributes.${index}.range_max`)
                }}
                error={!!errors?.attributes?.[index]?.range_min}
                helperText={
                  errors?.attributes?.[index]?.range_min?.message ||
                  attributeWarnings?.[attribute.name]?.range_min ||
                  ''
                }
              />
            </Tooltip>
          )}
        />
      </Box>
      <Box sx={sxStyles.controllerWithWrapperBox}>
        <Typography>To</Typography>
        <Controller
          name={`attributes.${index}.range_max`}
          control={control}
          defaultValue={attribute.range_max}
          render={({ field }) => (
            <Tooltip
              title={getRangeTooltipMessage(
                validationRules,
                attribute.name,
                'range_max'
              )}
              arrow
              placement="top"
            >
              <TextField
                sx={{
                  ...sxStyles.textFieldStyle,
                  ...sxStyles.leftHelperTextStyle,
                  '& .MuiInputBase-root': {
                    ...sxStyles.textFieldStyle['& .MuiInputBase-root'],
                    backgroundColor: isRangeMaxDirty
                      ? (theme) => theme.palette.custom.inputChanged
                      : 'unset',
                  },
                  ...(attributeWarnings?.[attribute.name]?.range_max &&
                    sxStyles.warningColor),
                }}
                {...field}
                onChange={(e) => {
                  field.onChange(e.target.value.trim())
                  trigger(`attributes.${index}.range_max`)
                }}
                error={!!errors?.attributes?.[index]?.range_max}
                helperText={
                  errors?.attributes?.[index]?.range_max?.message ||
                  attributeWarnings?.[attribute.name]?.range_max ||
                  ''
                }
              />
            </Tooltip>
          )}
        />
      </Box>
    </Box>
  )
}

interface IBlendParameters {
  attributes?: TAttribute[]
  mode: 'edit' | 'disabled'
  displayHeader?: boolean
  displayAttributeValue?: boolean
}

export const BlendParameters: React.FC<IBlendParameters> = ({
  attributes,
  mode,
  displayHeader = false,
  displayAttributeValue = false,
}) => {
  const [attributeWarnings, setAttributeWarnings] = useState<
    Record<
      string,
      { range_min?: string; range_max?: string; range_avg?: string }
    >
  >({})

  const {
    control,
    formState: { errors, dirtyFields },
    trigger,
  } = useFormContext()
  const watchedAttributes = useWatch({ name: 'attributes', control })

  const validationRules = useSelector(
    (state: RootState) => state.validations.validationRules
  )

  useEffect(() => {
    const warnings = getBlendsAttributeWarnings(
      watchedAttributes,
      validationRules
    )
    setAttributeWarnings(warnings)
  }, [watchedAttributes, validationRules])

  return (
    <Card p="16px">
      <CardTitle title="Parameters" />
      <MainBox>
        {displayHeader && (
          <HeaderContainer>
            <HeaderParagraph>&nbsp;</HeaderParagraph>
            <HeaderParagraph>Range</HeaderParagraph>
            {displayAttributeValue && <HeaderParagraph>Value</HeaderParagraph>}
          </HeaderContainer>
        )}
        {attributes?.map((attribute, index) => (
          <BodyContainer key={attribute.name}>
            <Typography sx={sxStyles.typographyNameStyle}>
              {attribute.name}
            </Typography>
            {mode === 'edit'
              ? editAbleRow(
                  attribute,
                  index,
                  control,
                  errors,
                  dirtyFields,
                  trigger,
                  attributeWarnings,
                  validationRules
                )
              : disabledRow(attribute)}
            {displayAttributeValue && (
              <Typography>{attribute.value ?? '-'}</Typography>
            )}
          </BodyContainer>
        ))}
      </MainBox>
    </Card>
  )
}
